import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import { Button, notification } from "antd";
import B2C2BApi from "../../../api-services/b2c2b-api.service";
import CustomerApi from "../../../api-services/customer-api.service";
import ListApi from "../../../api-services/list-api.service";
import LandToursApi from "../../../api-services/land-tours-api.service";
import { LandTourCategory, LandTourDetail } from "../../../interfaces/land-tour.interface";
import { withMalaysiaFirst } from "../../../utils/helpers";
import { useAuth } from "../../../services/AuthContext";
import { DTA_TYPE } from "../../../enums/dta.enum";
import { PAYMENT_CHANNELS } from "../b2c2b/payment/PaymentMethodPanel";
import { DateSelection, PaxCounts, carriedDateSelection } from "./components/ChooseYourTour";
import {
  Agent, DEFAULT_CHANGE_REASONS, FlightDetails, Passenger, bindingAgent, dobDisabled, effectivePassengers, emptyPassenger, flightSummary, passengerCategories,
  INVALID_ROOM_MESSAGE, passengerMissing, roomConfigurations, travellersSummary,
} from "./booking/bookingUtils";
import useBookingQuote from "./booking/useBookingQuote";
import PriceBreakdownModal, { buildBreakdown } from "./booking/PriceBreakdownModal";
import BookingStepper from "./booking/BookingStepper";
import BookingSummaryCard, { SummaryRowKey } from "./booking/BookingSummaryCard";
import BookingFooter from "./booking/BookingFooter";
import SelectionPage, { SelectionErrors } from "./booking/SelectionPage";
import TravellersStep from "./booking/TravellersStep";
import PartnerStep from "./booking/PartnerStep";
import PaymentStep, { InvoiceAddress, normaliseBankOptions } from "./booking/PaymentStep";
import { MobileFooter, MobileProgressHeader } from "./booking/MobileChrome";
import useBreakpoint from "../../../utils/useBreakpoint";

type Stage = "selection" | "edit" | "travellers" | "partner" | "payment";

interface Selection {
  category: LandTourCategory;
  selectedDate: DateSelection;
  paxCounts: PaxCounts;
}

interface Props {
  landTour: LandTourDetail;
  category: LandTourCategory;
  selectedDate: DateSelection;
  paxCounts: PaxCounts;
  /** Leave the checkout and return to the show page. */
  onClose: () => void;
  /** Edit Selection > Save: hand the new category/date/pax back so the show page stays in sync. */
  onSelectionChange: (category: LandTourCategory, date: DateSelection, counts: PaxCounts) => void;
  /** Returning from sign-in: reopen on the partner step with what the customer had entered. */
}

const emptyFlights = (): FlightDetails => ({
  flight_departure_date: "",
  depart_origin_destination: "",
  departure_flight_no: "",
  depart_dept_arrival_time: "",
  flight_return_date: "",
  return_origin_destination: "",
  return_flight_no: "",
  return_departure_arrv_time: "",
});

const emptyAddress: InvoiceAddress = { address_1: "", address_2: "", city_name: "", state: "", country: "", postal_code: "" };

// Only the fields the bookings endpoint permits; UI-only keys (same_as_first, file names) stay out of the request.
const bookingPayload = (passenger: Passenger) => ({
  designation: passenger.designation,
  surname: passenger.surname,
  given_name: passenger.given_name,
  mobile: passenger.mobile,
  email: passenger.email,
  category: passenger.category,
  nationality: passenger.nationality,
  date_of_birth: passenger.date_of_birth,
  customer_remark: passenger.customer_remark,
  passport_number: passenger.passport_number,
  as_passport_photocopy: passenger.as_passport_photocopy,
  wechat_qrcode: passenger.wechat_qrcode,
});

const partnerLabel = (agent: Agent | null) => (agent ? agent.agency_full_name || agent.name : undefined);

export default function GroundTourBookingFlow({ landTour, category, selectedDate, paxCounts, onClose, onSelectionChange }: Props) {
  const { username } = useAuth();
  const isMobile = useBreakpoint();
  const [stage, setStage] = useState<Stage>("selection");
  const [returnStage, setReturnStage] = useState<Stage>("travellers");
  const [selection, setSelection] = useState<Selection>({ category, selectedDate, paxCounts });
  const snapshot = useRef<{ selection: Selection; room: string; language: string; flights: FlightDetails } | null>(null);

  const categories = useMemo(() => passengerCategories(selection.paxCounts), [selection.paxCounts]);
  const [passengers, setPassengers] = useState<Passenger[]>(() => categories.map(emptyPassenger));
  const roomOptions = useMemo(() => roomConfigurations(selection.paxCounts), [selection.paxCounts]);
  const languages = landTour.languages || [];
  const [room, setRoom] = useState("");
  const [language, setLanguage] = useState(languages.length === 1 ? languages[0] : "");
  const [flights, setFlights] = useState<FlightDetails>(emptyFlights());
  const [selectionErrors, setSelectionErrors] = useState<SelectionErrors>({});
  // Edit Selection: the new category does not depart on the chosen date, so the customer must pick another.
  const [dateCleared, setDateCleared] = useState(false);
  const [showPassengerErrors, setShowPassengerErrors] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [stickyAgent, setStickyAgent] = useState<Agent | null>(null);
  const [stickyDta, setStickyDta] = useState<string>(DTA_TYPE.REFERRAL);
  const [referralAgent, setReferralAgent] = useState<any>(null);
  const [referralReleased, setReferralReleased] = useState(false);
  const [reasons, setReasons] = useState(DEFAULT_CHANGE_REASONS);
  const [stickyRequesting, setStickyRequesting] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<Record<string, any>>({});
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);
  const [states, setStates] = useState<{ value: string; label: string }[]>([]);
  const [address, setAddress] = useState<InvoiceAddress>(emptyAddress);
  const [gateway, setGateway] = useState("");
  const [bank, setBank] = useState("");
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const roomChoice = roomOptions.find((option) => option.label === room);
  const effective = effectivePassengers(passengers);

  // Keep passengers aligned with the headcount, preserving what was already typed for unchanged slots.
  useEffect(() => {
    setPassengers((previous) => categories.map((cat, index) => previous[index]?.category === cat ? previous[index] : emptyPassenger(cat)));
  }, [categories.join("|")]);

  useEffect(() => {
    if (room && !roomOptions.some((option) => option.label === room)) setRoom("");
  }, [roomOptions.map((option) => option.label).join("|")]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [stage]);

  useEffect(() => {
    setAgentsLoading(true);
    const listApi = new ListApi();
    Promise.all([B2C2BApi.getEligibleAgents(), B2C2BApi.getAllPaymentMethods(), listApi.getCountries(), listApi.getStates({ country: "Malaysia" }, true)])
      .then(([eligibleAgents, methods, countryOptions, stateOptions]) => {
        setAgents(eligibleAgents);
        setPaymentMethods(methods || {});
        setCountries(withMalaysiaFirst(countryOptions));
        setStates(stateOptions);
        const stored = Cookies.get("agency");
        if (stored) {
          try {
            const cookie = JSON.parse(stored);
            const preferred = { ...cookie.masterAgent, link: cookie.link, subagent: cookie.subagent };
            const match = { ...(eligibleAgents.find((agent) => String(agent.id) === String(preferred.id)) || preferred), link: cookie.link, subagent: cookie.subagent };
            setReferralAgent(match);
            setSelectedAgent(match);
          } catch { Cookies.remove("agency"); }
        }
      })
      .catch(() => notification.error({ message: "Unable to load booking options." }))
      .finally(() => setAgentsLoading(false));
  }, []);

  // A customer already bound to a partner (sticky) is preselected; logged-in customers are looked up by their account email.
  useEffect(() => {
    if (stage !== "partner") return;
    // No binding for this email (changed to an unbound one): drop the sticky partner and go back to the referral
    // link partner from the cookie, or to nothing so the customer picks from the list.
    const releaseSticky = () => {
      if (!stickyAgent) return;
      setStickyAgent(null);
      setStickyDta(DTA_TYPE.REFERRAL);
      setSelectedAgent((current) => {
        if (!current || String(current.id) !== String(stickyAgent.id)) return current;
        return referralAgent && !referralReleased ? referralAgent : null;
      });
    };
    const emailLookup = (email: string) => {
      if (!email) return releaseSticky();
      B2C2BApi.getCustomerReferralBinding(email).then((response) => {
        const agent = bindingAgent(response, agents);
        if (!agent) return releaseSticky();
        const attrs = response?.data?.attributes || response?.data || response;
        setStickyAgent(agent);
        setStickyDta(attrs?.source === "self_select" ? DTA_TYPE.DIRECT : DTA_TYPE.REFERRAL);
        setSelectedAgent(agent);
      }).catch(releaseSticky);
    };
    if (username) {
      CustomerApi.getProfile().then((customer: any) => {
        emailLookup(customer?.email || "");
        // The backend owns the wording of the release reasons (StickyAgentChangeRequests::Create.reason_texts).
        const texts = customer?.change_reasons;
        if (texts && Object.keys(texts).length) setReasons(Object.entries(texts).map(([value, label]) => ({ value, label: String(label) })));
      }).catch(() => undefined);
    }
    else emailLookup(effective[0]?.email || "");
  }, [stage, username, agents.length]);

  const quoteArgs = {
    landTourId: landTour.id,
    categoryId: selection.category.id,
    departDate: selection.selectedDate.date,
    paxCounts: selection.paxCounts,
    totalRooms: roomChoice?.total || roomOptions[0]?.total || 0,
    nationalities: effective.map((passenger) => passenger.nationality),
  };
  const { quote, loading: quoteLoading, failed: quoteFailed } = useBookingQuote({ ...quoteArgs, enabled: !dateCleared });
  const { total: localTotal } = useMemo(
    () => buildBreakdown({ landTour, priceDate: selection.selectedDate.priceDate, paxCounts: selection.paxCounts, quote: null, nationalities: quoteArgs.nationalities }),
    [landTour, selection, quoteArgs.nationalities.join("|")],
  );
  const total = quote ? quote.total : quoteFailed ? localTotal : undefined;

  // Credit/debit card adds the admin fee (the backend charges the same rate on the amount), unless a zero CC rate fair is on.
  const ccRate = landTour.ccRate || 0;
  const adminFee = stage === "payment" && gateway === "public_bank_2" && total !== undefined && ccRate > 0
    ? Math.round(total * ccRate * 100) / 100
    : 0;
  const payableTotal = total === undefined ? undefined : total + adminFee;

  const dtaTypeFor = (agent: Agent | null): string => {
    if (agent && stickyAgent && String(agent.id) === String(stickyAgent.id)) return stickyDta;
    if (agent && referralAgent && String(agent.id) === String(referralAgent.id)) return DTA_TYPE.REFERRAL;
    return DTA_TYPE.DIRECT;
  };

  const updatePassenger = (index: number, values: Partial<Passenger>) => {
    setPassengers((previous) => previous.map((passenger, i) => i === index ? { ...passenger, ...values } : passenger));
  };

  // ---- selection / edit ----
  const goEdit = () => {
    snapshot.current = { selection, room, language, flights };
    setReturnStage(stage === "selection" ? "travellers" : stage);
    setSelectionErrors({});
    setDateCleared(false);
    setStage("edit");
  };

  const cancelEdit = () => {
    if (snapshot.current) {
      setSelection(snapshot.current.selection);
      setRoom(snapshot.current.room);
      setLanguage(snapshot.current.language);
      setFlights(snapshot.current.flights);
    }
    setDateCleared(false);
    setStage(returnStage);
  };

  const validateSelection = (): boolean => {
    if (dateCleared) {
      notification.warning({ message: "Please select a departure date." });
      return false;
    }
    if (roomOptions.length === 0) {
      notification.warning({ message: INVALID_ROOM_MESSAGE });
      return false;
    }
    const errors: SelectionErrors = {
      room: !room,
      language: languages.length > 0 && !language,
      departureDate: !landTour.incFlight && !flights.flight_departure_date,
    };
    setSelectionErrors(errors);
    if (errors.room || errors.language || errors.departureDate) {
      notification.warning({ message: "Please complete the highlighted selections." });
      return false;
    }
    return true;
  };

  const continueFromSelection = () => {
    if (validateSelection()) setStage("travellers");
  };

  const saveEdit = () => {
    if (!validateSelection()) return;
    onSelectionChange(selection.category, selection.selectedDate, selection.paxCounts);
    setStage(returnStage);
  };

  const changeCategory = (next: LandTourCategory) => {
    // Keep the chosen date when the new category departs that day too; otherwise the customer picks again.
    const carried = carriedDateSelection(next, selection.selectedDate, {
      bookingCutOffDay: landTour.bookingCutOffDay,
      departDay: landTour.departDay,
      minimumPax: Math.max(landTour.minBooking || 1, 1),
    });
    setDateCleared(!carried);
    setSelection((current) => ({ ...current, category: next, selectedDate: carried || current.selectedDate }));
  };

  // ---- steps ----
  const goNextFromTravellers = () => {
    const problems = effective.map((passenger, index) => ({
      index,
      missing: passengerMissing(passenger, index, landTour).length > 0,
      badAge: !!passenger.date_of_birth && dobDisabled(dayjs(passenger.date_of_birth), passenger.category, selection.selectedDate.date, landTour),
    }));
    const first = problems.find((item) => item.missing || item.badAge);
    if (first) {
      setShowPassengerErrors(true);
      notification.warning({
        message: first.badAge && !first.missing
          ? `Passenger ${first.index + 1}'s date of birth does not match the selected fare category.`
          : `Please complete the required details for Passenger ${first.index + 1}.`,
      });
      return;
    }
    setStage("partner");
  };

  const goNextFromPartner = () => {
    if (!selectedAgent) return notification.warning({ message: "Please select a preferred partner." });
    setStage("payment");
  };

  // Confirmation for leaving a referral partner lives in PartnerStep; by here the choice is final.
  const selectAgent = (agent: Agent) => setSelectedAgent(agent);

  const releaseReferral = () => {
    setReferralReleased(true);
    setSelectedAgent(null);
  };

  const requestStickyChange = async (reason: string, other: string): Promise<boolean> => {
    setStickyRequesting(true);
    try {
      const response = await B2C2BApi.createStickyAgentChangeRequest({ change_reason: reason, other_reason: other });
      notification.success({ message: response?.message || "Request Submitted. You can choose a new agent." });
      return true;
    } catch (error: any) {
      const errors = error?.response?.data?.errors;
      notification.error({ message: Array.isArray(errors) ? errors.join(" ") : error?.response?.data?.error || "Unable to submit the change request." });
      return false;
    } finally {
      setStickyRequesting(false);
    }
  };

  const bankOptions = useMemo(() => {
    const channel = PAYMENT_CHANNELS.find((item) => item.value === gateway);
    return normaliseBankOptions(channel ? paymentMethods[channel.bankName] || [] : []);
  }, [gateway, paymentMethods]);

  const submit = async () => {
    if (!gateway || (bankOptions.length && !bank) || !termsAgreed || !privacyAgreed || !selectedAgent) {
      return notification.warning({ message: "Please complete the payment method and agreements." });
    }
    const bankOption = bankOptions.find((option: any) => option.value === bank);
    let method = bank || gateway;
    let bankType = "";
    if (gateway === "dobw" && method.includes(" - ")) [method, bankType] = method.split(" - ");
    setSubmitting(true);
    try {
      const response = await B2C2BApi.saveLandTourBooking({
        tour_id: landTour.id,
        category_id: selection.category.id,
        depart_date: selection.selectedDate.date,
        rooms: roomChoice?.label || room,
        total_rooms: roomChoice?.total || 0,
        language,
        booking_prices: Object.entries(selection.paxCounts).filter(([, quantity]) => quantity > 0).map(([name, quantity]) => ({ name, quantity })),
        bookings: effective.map(bookingPayload),
        flights: landTour.incFlight ? [] : [flights],
        agent_id: selectedAgent.id,
        register_as_member: !username,
        business_type: "b2c2b",
        dta_type: dtaTypeFor(selectedAgent),
        ...(referralAgent ? { referral_agent_id: referralAgent.id, referral_link: referralAgent.link, new_agent_id: selectedAgent.id } : {}),
        ...address,
        payment: { gateway, method, bank_type: bankType, redirect_url: bankOption?.redirect_url || "" },
      });
      if (response?.error) throw new Error(response.error);
      window.location.href = response.redirect_to;
    } catch (error: any) {
      notification.error({ message: error?.response?.data?.error || error?.message || "Unable to create the booking." });
      setSubmitting(false);
    }
  };

  // ---- render ----
  const breakdownModal = (
    <PriceBreakdownModal
      open={breakdownOpen}
      onClose={() => setBreakdownOpen(false)}
      landTour={landTour}
      priceDate={selection.selectedDate.priceDate}
      paxCounts={selection.paxCounts}
      quote={quote}
      nationalities={quoteArgs.nationalities}
      adminFee={adminFee}
      ccRate={ccRate}
    />
  );

  if (stage === "selection" || stage === "edit") {
    return (
      <>
        <SelectionPage
          mode={stage === "edit" ? "edit" : "customize"}
          landTour={landTour}
          category={selection.category}
          selectedDate={selection.selectedDate}
          paxCounts={selection.paxCounts}
          onCategoryChange={changeCategory}
          dateCleared={dateCleared}
          onDateChange={(date) => { setDateCleared(false); setSelection((current) => ({ ...current, selectedDate: date })); }}
          onPaxCountsChange={(counts) => setSelection((current) => ({ ...current, paxCounts: counts }))}
          roomOptions={roomOptions}
          room={room}
          onRoomChange={setRoom}
          language={language}
          onLanguageChange={setLanguage}
          flights={flights}
          onFlightsChange={setFlights}
          errors={selectionErrors}
          total={dateCleared ? undefined : total}
          totalLoading={!dateCleared && quoteLoading && !quote}
          onShowBreakdown={() => setBreakdownOpen(true)}
          onBack={stage === "edit" ? cancelEdit : onClose}
          onCancel={stage === "edit" ? cancelEdit : onClose}
          onContinue={stage === "edit" ? saveEdit : continueFromSelection}
        />
        {breakdownModal}
      </>
    );
  }

  const step = stage === "travellers" ? 1 : stage === "partner" ? 2 : 3;
  const onEditRow = (row: SummaryRowKey) => (row === "partner" ? setStage("partner") : goEdit());
  const goBack = () => setStage(stage === "travellers" ? "selection" : stage === "partner" ? "travellers" : "partner");

  const content = (
    <>
      {stage === "travellers" && (
        <TravellersStep
          landTour={landTour}
          departDate={selection.selectedDate.date}
          priceDate={selection.selectedDate.priceDate}
          paxCounts={selection.paxCounts}
          roomDisplay={roomChoice?.display || ""}
          language={language}
          flights={flights}
          passengers={passengers}
          countries={countries}
          willRegister={!username}
          showErrors={showPassengerErrors}
          onPassengerChange={updatePassenger}
          onEditSelection={goEdit}
        />
      )}
      {stage === "partner" && (
        <PartnerStep
          agents={agents}
          loading={agentsLoading}
          selected={selectedAgent}
          stickyAgent={stickyAgent}
          referralAgent={referralReleased ? null : referralAgent}
          signedIn={!!username}
          reasons={reasons}
          stickySubmitting={stickyRequesting}
          onRequestStickyChange={requestStickyChange}
          onReleaseReferral={releaseReferral}
          onSelect={selectAgent}
          onBackToDetails={() => setStage("travellers")}
        />
      )}
      {stage === "payment" && (
        <PaymentStep
          landTour={landTour}
          total={payableTotal}
          summary={{
            departureDate: selection.selectedDate.date,
            travellers: travellersSummary(selection.paxCounts),
            partner: partnerLabel(selectedAgent),
            room: roomChoice?.display,
            language,
            ...flightSummary(landTour, selection.selectedDate.date, flights),
            onEdit: goEdit,
          }}
          address={address}
          onAddressChange={setAddress}
          countries={countries}
          states={states}
          paymentMethods={paymentMethods}
          gateway={gateway}
          onGatewayChange={(value) => { setGateway(value); setBank(""); }}
          bank={bank}
          onBankChange={setBank}
          termsAgreed={termsAgreed}
          onTermsChange={setTermsAgreed}
          privacyAgreed={privacyAgreed}
          onPrivacyChange={setPrivacyAgreed}
        />
      )}
    </>
  );

  const nextDisabled = stage === "partner" && (agentsLoading || (!selectedAgent && agents.length === 0));
  const advance = stage === "travellers" ? goNextFromTravellers : goNextFromPartner;

  if (isMobile) {
    return (
      <div className="gt-page gt-m gt-steps">
        <MobileProgressHeader step={(step + 1) as 2 | 3 | 4} onBack={goBack} />
        <div className="gt-m-body">{content}</div>
        <MobileFooter stacked={stage === "travellers"} total={payableTotal} loading={quoteLoading && !quote} onShowBreakdown={() => setBreakdownOpen(true)}>
          {stage === "payment" ? (
            <Button type="primary" size="large" loading={submitting} disabled={total === undefined} onClick={submit}>Make Payment</Button>
          ) : (
            <Button type="primary" size="large" block={stage === "travellers"} disabled={nextDisabled} onClick={advance}>Next</Button>
          )}
        </MobileFooter>
        {breakdownModal}
      </div>
    );
  }

  return (
    <div className="gt-page gt-steps">
      <div className="gt-container">
        <BookingStepper step={step as 1 | 2 | 3} />

        <div className="gt-layout">
          <div className="gt-layout__main">{content}</div>

          <BookingSummaryCard
            title={selection.category.category}
            departureDate={dayjs(selection.selectedDate.date).format("DD MMM YYYY")}
            travellers={travellersSummary(selection.paxCounts)}
            room={roomChoice?.display}
            language={language}
            partner={stage === "payment" ? partnerLabel(selectedAgent) : undefined}
            total={payableTotal}
            adminFee={adminFee}
            ccRate={ccRate}
            loading={quoteLoading && !quote}
            onShowBreakdown={() => setBreakdownOpen(true)}
            onEdit={stage === "travellers" ? undefined : onEditRow}
          />
        </div>
      </div>

      <BookingFooter>
        <Button size="large" onClick={goBack}>Back</Button>
        {stage === "payment" ? (
          <Button size="large" type="primary" loading={submitting} disabled={total === undefined} onClick={submit}>Pay Now</Button>
        ) : (
          <Button size="large" type="primary" disabled={nextDisabled} onClick={advance}>Next</Button>
        )}
      </BookingFooter>
      {breakdownModal}
    </div>
  );
}
